import classnames from 'classnames';
import React from 'react';
import { ApolloError } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import { useEventSystem } from '../EventProvider/EventProvider';
import ScopusHOC from '../ScopusHOC/ScopusHOC';

import styles from './Error.module.scss';

interface ErrorProps {
  error: Error | ApolloError;
  errorText?: string;
  className?: string;
  refresh?: () => void;
}

const Error = React.memo(({ className, error, errorText, refresh }: ErrorProps) => {
  const { dispatchEvent } = useEventSystem();
  const intl = useIntl();

  React.useEffect(() => {
    dispatchEvent('errorThrown', { error });
  }, [dispatchEvent, error]);

  const defaultErrorText = intl.formatMessage({
    id: 'error.defaultErrorMessage',
    defaultMessage: 'Something went wrong… Refresh, or try again later.',
    description: 'Error component. Default text that is displayed when an error happens.',
  });

  return (
    <ScopusHOC withExperimentalScript={false}>
      <div className={classnames(styles.root, className)}>
        <div className={styles.container}>
          <els-icon class={styles.icon} size="l" color="currentColor" icon-name="unlink" />
          <span className={styles.text} data-testid="errorText">
            {errorText ?? defaultErrorText}
            {refresh ? (
              <button
                type="button"
                className={classnames(styles.button, 'button--link button-large')}
                onClick={() => refresh()}
              >
                <span className="button__text">
                  <FormattedMessage
                    id="error.refreshButtonLabel"
                    defaultMessage="Refresh"
                    description="Error component. Label of the Refresh button"
                  />
                </span>
              </button>
            ) : null}
          </span>
        </div>
      </div>
    </ScopusHOC>
  );
});
Error.displayName = 'Error';

export default Error;
